/**
 * Quill Chat streaming hooks with feature-flag toggle between SSE and NDJSON.
 *
 * SSE hooks use hand-written {@link useStreamRequest} for the Quill SSE
 * endpoints (:sendMessageStream and :resumeStreamSse).
 *
 * NDJSON hooks delegate to the generated streaming hooks from
 * `@/generated/hooks/zen/streaming/thread_service`.
 *
 * A Statsig gate (`quill_sse_streaming`) selects which implementation is
 * returned to consumers. Both hooks are called unconditionally to satisfy
 * React's rules of hooks.
 */

import { useCallback, useRef } from 'react';

import { useGateValue } from '@statsig/react-bindings';

import { useAuth } from '@/shared/auth/hooks/use-auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import {
  useStreamRequest,
  EStreamProtocol,
  type IStreamError,
  type IMessageMeta,
} from '@/shared/streaming';
import { FEATURE_FLAGS } from '@/shared/types/features';
import {
  useStreamingSendMessage as useGeneratedSendMessage,
  useStreamingResumeMessageStream as useGeneratedResumeMessageStream,
} from '@/generated/hooks/zen/streaming/thread_service';
import type {
  V1ThreadMessage,
  ThreadServiceSendMessageStreamBody,
  ThreadServiceResumeMessageStreamSseBody,
} from '@/generated/hooks/zen/models';
import { ENDPOINTS } from '@/generated/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IQuillStreamingOptions {
  onMessage?: (event: V1ThreadMessage) => void;
  onComplete?: (events: V1ThreadMessage[]) => void;
  onError?: (error: Error) => void;
  timeoutMs?: number;
  maxRetries?: number;
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

function buildEndpointUrl(
  endpointPath: string,
  params: Record<string, string>,
): string {
  return endpointPath.replace(/\{(\w+)\}/g, (_, key) => params[key]);
}

// ---------------------------------------------------------------------------
// SSE: useSSESendMessage (internal)
// ---------------------------------------------------------------------------

function useSSESendMessage(options?: IQuillStreamingOptions) {
  const { getAccessTokenSilently } = useAuth();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const {
    send,
    isLoading,
    cancel,
    error: _error,
  } = useStreamRequest<V1ThreadMessage>({
    protocol: EStreamProtocol.SSE,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      getToken: () => getAccessTokenSilently(),
      e2eTestMode: isE2ETestMode(),
    },
    retry: {
      maxAttempts: optionsRef.current?.maxRetries ?? 3,
    },
    timeoutMs: optionsRef.current?.timeoutMs,
    observability: {
      module: 'quill-chat',
      operation: 'send_message_sse',
    },
    onMessage: (data: V1ThreadMessage, meta: IMessageMeta) => {
      // Filter out the SSE `event: end` terminator — it carries no data
      if (meta.eventType === 'end') {
        return;
      }
      optionsRef.current?.onMessage?.(data);
    },
    onComplete: (events: V1ThreadMessage[]) => {
      // The streaming library accumulates events before the onMessage filter,
      // so end-event data ({}) may be included — strip it out.
      const filtered = events.filter(e => e.name);
      optionsRef.current?.onComplete?.(filtered);
    },
    onError: (streamError: IStreamError) => {
      // Bridge IStreamError → Error for consumers
      optionsRef.current?.onError?.(streamError.error);
    },
  });

  const sendMessage = useCallback(
    (
      organization: string,
      workspace: string,
      automation: string,
      agent: string,
      thread: string,
      data: ThreadServiceSendMessageStreamBody,
    ): Promise<V1ThreadMessage[]> => {
      const url = buildEndpointUrl(ENDPOINTS.SendMessageStream.path, {
        organization,
        workspace,
        automation,
        agent,
        thread,
      });
      return send({ url, body: data });
    },
    [send],
  );

  return {
    sendMessage,
    cancelCurrentStream: cancel,
    isLoading,
  };
}

// ---------------------------------------------------------------------------
// SSE: useSSEResumeMessageStream (internal)
// ---------------------------------------------------------------------------

function useSSEResumeMessageStream(options?: IQuillStreamingOptions) {
  const { getAccessTokenSilently } = useAuth();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const {
    send,
    isLoading,
    cancel,
    error: _error,
  } = useStreamRequest<V1ThreadMessage>({
    protocol: EStreamProtocol.SSE,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    auth: {
      getToken: () => getAccessTokenSilently(),
      e2eTestMode: isE2ETestMode(),
    },
    retry: {
      maxAttempts: optionsRef.current?.maxRetries ?? 3,
    },
    timeoutMs: optionsRef.current?.timeoutMs,
    observability: {
      module: 'quill-chat',
      operation: 'resume_message_stream_sse',
    },
    onMessage: (data: V1ThreadMessage, meta: IMessageMeta) => {
      if (meta.eventType === 'end') {
        return;
      }
      optionsRef.current?.onMessage?.(data);
    },
    onComplete: (events: V1ThreadMessage[]) => {
      // The streaming library accumulates events before the onMessage filter,
      // so end-event data ({}) may be included — strip it out.
      const filtered = events.filter(e => e.name);
      optionsRef.current?.onComplete?.(filtered);
    },
    onError: (streamError: IStreamError) => {
      optionsRef.current?.onError?.(streamError.error);
    },
  });

  const resumeMessageStream = useCallback(
    (
      organization: string,
      workspace: string,
      automation: string,
      agent: string,
      thread: string,
      data: ThreadServiceResumeMessageStreamSseBody,
    ): Promise<V1ThreadMessage[]> => {
      const url = buildEndpointUrl(ENDPOINTS.ResumeMessageStreamSse.path, {
        organization,
        workspace,
        automation,
        agent,
        thread,
      });
      return send({ url, body: data });
    },
    [send],
  );

  return {
    resumeMessageStream,
    cancelCurrentStream: cancel,
    isLoading,
  };
}

// ---------------------------------------------------------------------------
// Public: useStreamingSendMessage (gate-switched)
// ---------------------------------------------------------------------------

/**
 * Hook for sending a message to a Quill thread via streaming.
 *
 * Uses SSE when the `quill_sse_streaming` gate is enabled, otherwise falls
 * back to the generated NDJSON streaming hook.
 */
export function useStreamingSendMessage(options?: IQuillStreamingOptions) {
  const isSSEEnabled = useGateValue(FEATURE_FLAGS.ENABLE_QUILL_SSE_STREAMING);

  // Both hooks called unconditionally (React rules of hooks)
  const sse = useSSESendMessage(options);
  const ndjson = useGeneratedSendMessage(options);

  if (isSSEEnabled) {
    return sse;
  }

  return {
    sendMessage: ndjson.sendMessage,
    cancelCurrentStream: ndjson.cancelCurrentStream,
    isLoading: ndjson.isLoading,
  };
}

// ---------------------------------------------------------------------------
// Public: useStreamingResumeMessageStream (gate-switched)
// ---------------------------------------------------------------------------

/**
 * Hook for resuming a Quill thread's stream after a disconnect.
 *
 * Uses SSE when the `quill_sse_streaming` gate is enabled, otherwise falls
 * back to the generated NDJSON streaming hook.
 */
export function useStreamingResumeMessageStream(
  options?: IQuillStreamingOptions,
) {
  const isSSEEnabled = useGateValue(FEATURE_FLAGS.ENABLE_QUILL_SSE_STREAMING);

  // Both hooks called unconditionally (React rules of hooks)
  const sse = useSSEResumeMessageStream(options);
  const ndjson = useGeneratedResumeMessageStream(options);

  if (isSSEEnabled) {
    return sse;
  }

  return {
    resumeMessageStream: ndjson.resumeMessageStream,
    cancelCurrentStream: ndjson.cancelCurrentStream,
    isLoading: ndjson.isLoading,
  };
}
